import tw from 'twin.macro'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CgPen } from 'react-icons/cg'

import { ONE_TIME_PRODUCT, STRIPE_SETUP } from 'modules/App/components/Header'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import {
  useListenCoachOrgSlugQuery,
  useListenDraftPrimaryColorQuery,
  useListenPublishedCheckoutPageQuery,
  useListenPublishedSalesPageQuery,
  useListenSalesImagesQuery,
  useListenSalesPageQuery,
  useListenSalesPageSeoQuery,
  usePublishSalesPageMutation,
  useUpdateSalesPageSeoMutation,
} from '../pageBuilderApi'
import {
  useListenOneTimeProductQuery,
  useListenOneTimeProductSlugQuery,
  useListenStripeProductIdQuery,
} from 'modules/ProductInfo/productInfoApi'
import { getIsProductReadyForSale } from 'modules/ProductInfo/hooks/useReadyForSaleTasks'
import { PRODUCT_SHARE_GATE_MESSAGE } from 'modules/ProductInfo/constants/shareConstants'

import { useAlert } from 'common/components/Alert/hooks/useAlert'
import Loading from 'modules/App/components/Loading'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { buttonBase } from 'common/components/Button/Button'
import { ShareLinkMenu } from 'modules/ProductInfo/components/ShareLinkMenu/ShareLinkMenu'
import { SeoForm } from '../components/SeoForm/SeoForm'
import SalesPagePreview from '../components/PagePreview/SalesPagePreview'

export const PAGE_MAX_WIDTH = 1440

export default function SalesPageBuilder() {
  const navigate = useNavigate()
  const { productId } = useParams()

  const { userId } = useAuth()
  const { createAlert } = useAlert()
  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''

  const [isPublishing, setIsPublishing] = useState(false)

  const { data: salesPage } = useListenSalesPageQuery({ coachOrgId, productId })
  const pageIsLoading = salesPage === undefined || salesPage?.isLoading
  const { data: primaryColorData } = useListenDraftPrimaryColorQuery({ coachOrgId, productId })
  const primaryColorLoading = primaryColorData === undefined || primaryColorData?.isLoading
  const { data: seoData } = useListenSalesPageSeoQuery({ coachOrgId, productId })
  const seoLoading = seoData === undefined || seoData?.isLoading
  const { data: coachOrgSlug } = useListenCoachOrgSlugQuery({ coachOrgId })
  const coachOrgSlugLoading = coachOrgSlug === undefined || coachOrgSlug?.isLoading
  const { data: salesImageData } = useListenSalesImagesQuery({ coachOrgId, productId })
  const imagesLoading = salesImageData === undefined || salesImageData?.isLoading
  const { data: stripeProductId } = useListenStripeProductIdQuery({ coachOrgId })
  const stripeProductIdLoading = stripeProductId === undefined || stripeProductId?.isLoading
  const { data: productSlug } = useListenOneTimeProductSlugQuery({ coachOrgId, productId })
  const productSlugLoading = productSlug === undefined || productSlug?.isLoading
  const { data: publishedSalesPage } = useListenPublishedSalesPageQuery({ coachOrgId, productId })
  const publishedSalesPageLoading = publishedSalesPage === undefined || publishedSalesPage?.isLoading

  // This builder serves two pages with two different links, and they are shared under two
  // different rules.
  //
  // A product's sales page copies the product's storefront link, which works as soon as the
  // product is ready for sale — publishing this page is not part of that. The org's own
  // sales page copies the storefront root, which is this page, so there the rule really is
  // that it has to be published first.
  const { data: oneTimeProduct } = useListenOneTimeProductQuery({ coachOrgId, productId }, { skip: !productId })
  const { data: productPublishedCheckoutPage } = useListenPublishedCheckoutPageQuery(
    { coachOrgId, productId },
    { skip: !productId }
  )
  const isProductReadyForSale = getIsProductReadyForSale({
    product: oneTimeProduct?.isLoading ? null : oneTimeProduct,
    publishedCheckoutPage: productPublishedCheckoutPage?.isLoading ? null : productPublishedCheckoutPage,
  })
  const canShareLink = productId ? isProductReadyForSale : Boolean(publishedSalesPage)
  const shareGateMessage = productId ? PRODUCT_SHARE_GATE_MESSAGE : 'Publish this page before sharing the link'

  const [publishPage] = usePublishSalesPageMutation()
  const [updateSeo] = useUpdateSalesPageSeoMutation()

  const handlePublishPage = async () => {
    setIsPublishing(true)
    const { error } = await publishPage({ coachOrgId, productId })
    setIsPublishing(false)

    if (error) {
      createAlert({ text: 'Sales page could not be published. Please try again.', type: 'danger' })
      return
    }

    createAlert({ text: 'Sales page is published!', type: 'success' })
  }

  useEffect(() => {
    if (!stripeProductIdLoading && !stripeProductId) {
      createAlert({ text: 'Please create Stripe product first', type: 'danger' })
      navigate(STRIPE_SETUP)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeProductIdLoading, stripeProductId])

  useEffect(() => {
    if (!coachOrgSlugLoading && !coachOrgSlug) {
      createAlert({ text: 'Please create your link first', type: 'danger' })
      navigate(STRIPE_SETUP)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachOrgSlugLoading, coachOrgSlug])

  if (
    pageIsLoading ||
    primaryColorLoading ||
    coachOrgSlugLoading ||
    seoLoading ||
    imagesLoading ||
    stripeProductIdLoading ||
    productSlugLoading ||
    publishedSalesPageLoading
  ) {
    return (
      <div className='mt-12 bg-white rounded-xl p-8 border-2 border-gray-200' css={tw`max-w-[${PAGE_MAX_WIDTH}]`}>
        <Loading showHeader={false} containerClasses='!bg-white' />
      </div>
    )
  }

  return (
    <div className='mt-12 bg-white rounded-xl p-8 border-2 border-gray-200' css={tw`max-w-[${PAGE_MAX_WIDTH}]`}>
      <div className='flex flex-col min-h-screen bg-white'>
        <div className='w-full mx-auto'>
          <div className='flex items-center justify-between mb-5'>
            <div className='w-1/2'>
              <Dialog>
                <DialogTrigger css={[buttonBase]} className='text-tGray-med hover:text-tGreen'>
                  <CgPen className='w-4 h-4 mr-1' />
                  Edit SEO Settings
                </DialogTrigger>
                <DialogContent header='Edit SEO Settings'>
                  <FormRefsControlProvider>
                    <SeoForm
                      coachOrgId={coachOrgId}
                      productId={productId}
                      seo={seoData}
                      updateSeo={updateSeo}
                      uploadLocation={productId ? `oneTimeProducts/${productId}/salesPage` : 'salesPage'}
                      uploadType={productId ? 'one-time-seo-sales-image' : 'seo-sales-image'}
                    />
                  </FormRefsControlProvider>
                </DialogContent>
              </Dialog>
            </div>
            <ShareLinkMenu
              coachSlug={coachOrgSlug}
              pagePath={productId ? `/product/${productSlug || 'your-slug'}` : ''}
              blockedReason={canShareLink ? null : shareGateMessage}
              variant='button'
              setupHref={productId && !canShareLink ? `${ONE_TIME_PRODUCT}/${productId}` : null}
            />
          </div>
          <div className='flex flex-1 flex-col items-center relative'>
            <FormRefsControlProvider>
              <SalesPagePreview
                coachOrgId={coachOrgId}
                page={salesPage}
                handlePublishPage={handlePublishPage}
                isPublishing={isPublishing}
              />
            </FormRefsControlProvider>
          </div>
        </div>
      </div>
    </div>
  )
}
